import breakpoints from '../../../global/media-breakpoints';
import styles from './read-more-comp.module.scss';
import classNames from 'classnames';
import { resolveClassNames } from '../../../helpers/classes';
import { sanitizeHtml } from '../../../helpers/sanitize';
import { onEnterOrSpace } from '../../../global/helpers';
import debounce from 'lodash/debounce';
import { CSSProperties, ReactNode } from 'react';

type Props = {
  ellipsis?: boolean;
  hasButton?: boolean;
  buttonText?: string;
  buttonReadLessText?: string;
  id?: string;
  limit: number;
  parentAction?: (isExtended: boolean) => void;
  text?: string;
  inlineButton?: boolean;
  className?: string;
  buttonClass?: string | string[];
  buttonClassName?: string;
  style?: CSSProperties;
  styleButton?: CSSProperties;
  children?: ReactNode | ReactNode[];
  renderHTML?: boolean;
  ariaLabel?: string;
};

type State = {
  text: string;
  htmlCode: HTMLDivElement;
  ellipsis: string;
  buttonCopy: string;
  needReadMore: boolean;
  isExtended: boolean;
};

class ReadMoreComp extends React.Component<Props, State> {
  static displayName = 'ReadMoreComp';

  static defaultProps = {
    limit: 30,
    ellipsis: true,
    hasButton: false,
    inlineButton: false,
    className: 'info-with-links',
    renderHTML: true,
  };

  UNSAFE_componentWillMount() {
    if (this.props.children) {
      this.parseChildren(this.props, this.state.htmlCode);
    }
  }

  parseChildren = (props, currentNode) => {
    let charCount = 0;
    let parsingFinished = false;
    for (let i = 0; i < props.children.length; i++) {
      if (!parsingFinished) {
        let childNode = props.children;
        if (typeof childNode === 'string') {
          if (charCount + childNode.length > this.props.limit) {
            childNode =
              childNode.substr(0, this.props.limit - charCount) +
              this.state.ellipsis;
            parsingFinished = true;
          }
          charCount += childNode.length;
          currentNode.innerHTML = childNode;
          break;
        } else {
          for (let j = 0; j < childNode.length; j++) {
            if (childNode[j] && !parsingFinished) {
              const newNode = document.createElement(childNode[j].type);
              currentNode.appendChild(newNode);
              this.parseChildren(childNode[j].props, newNode);
            }
          }
        }
      }
    }
  };

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (this.props.text !== nextProps.text) {
      this.setState(this.getState(nextProps));
    }
  }

  getState = props => {
    let ellipsis = '';

    if (props.ellipsis && props.text.length > props.limit) {
      ellipsis = '…';
    }

    return {
      text: props.text ? props.text.substr(0, props.limit) + ellipsis : '',
      htmlCode: document.createElement('div'),
      ellipsis: ellipsis,
      buttonCopy: props.buttonText
        ? props.buttonText
        : window.polyglot.t('components.read_more.copy'),
      needReadMore:
        props.text && props.text.length <= props.limit ? false : true,
      isExtended: false,
    };
  };

  handleInteraction = event => {
    if (event.type === 'click' || event.type === 'keydown') {
      event.stopPropagation();
    }

    event.persist();
    this.delayedInteraction(event);
  };

  delayedInteraction = debounce(e => {
    const {
      hasButton,
      text,
      limit,
      buttonText,
      buttonReadLessText,
      parentAction,
    } = this.props;

    const { isExtended, ellipsis } = this.state;
    // If mobile/tablet or desktop => on click, on keydown events
    // If desktop => hover event
    if (
      (hasButton && e.type === 'click') ||
      (hasButton && e.type === 'keydown') ||
      (window.innerWidth < breakpoints.desktop && e.type === 'click') ||
      (window.innerWidth >= breakpoints.desktop &&
        (e.type === 'mouseenter' || e.type === 'mouseleave'))
    ) {
      if (!isExtended) {
        this.setState({
          text: text ? text : '',
          buttonCopy: buttonReadLessText
            ? buttonReadLessText
            : window.polyglot.t('components.read_more.read_less'),
          isExtended: true,
          needReadMore: !(text && text.length <= limit),
        });
      } else {
        this.setState({
          text: text ? text.substr(0, limit) + ellipsis : '',
          buttonCopy: buttonText
            ? buttonText
            : window.polyglot.t('components.read_more.copy'),
          isExtended: false,
          needReadMore: !(text && text.length <= limit),
        });
      }

      if (parentAction) {
        parentAction(!isExtended);
      }
    }
  }, 100);

  state = this.getState(this.props);

  render() {
    const {
      hasButton,
      renderHTML,
      className,
      style,
      id,
      children,
      inlineButton,
      buttonClass,
      buttonClassName,
      styleButton,
      ariaLabel,
    } = this.props;

    const { isExtended, htmlCode, buttonCopy, text } = this.state;

    const textElement = renderHTML ? (
      <span dangerouslySetInnerHTML={{ __html: sanitizeHtml(text) || '' }} />
    ) : (
      <span>{text}</span>
    );

    if (hasButton) {
      return (
        <div className={className} style={style} id={id}>
          {textElement}

          {!!isExtended && children && (
            <div
              dangerouslySetInnerHTML={{
                __html: htmlCode !== undefined ? htmlCode.innerHTML : '',
              }}
            />
          )}

          {!!inlineButton && <span>&nbsp;</span>}

          {this.state.needReadMore && (
            <button
              className={classNames(
                inlineButton
                  ? styles['read-more-inline']
                  : styles['read-more-block'],
                resolveClassNames(buttonClass, styles),
                buttonClassName,
              )}
              style={styleButton}
              onClick={this.handleInteraction}
              onKeyDown={onEnterOrSpace(this.handleInteraction)}
              aria-label={`${buttonCopy} ${ariaLabel ? ariaLabel : ''}`}
            >
              {this.state.buttonCopy}
            </button>
          )}
        </div>
      );
    }

    return (
      <div
        onMouseEnter={this.handleInteraction}
        onMouseLeave={this.handleInteraction}
        onClick={this.handleInteraction}
        className={className}
        style={style}
      >
        {textElement}
      </div>
    );
  }
}

export default ReadMoreComp;
